import type * as React from "react";
import { Box, Typography } from "@mui/material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import PointerRightIcon from "assets/images/cologuard/pointer-right.svg";
import QuestionCircleIcon from "assets/images/cologuard/question-circle.svg";
import UserLoveIcon from "assets/images/cologuard/user-love-2.svg";
import GLButton from "components/v3/sharedComponents/GLButton";
import ModalWrapper from "components/v2/sharedComponents/Modals/ModalComponents/ModalWrapper";
import { COLORS } from "components/v3/Theme/colors";
import Translate from "translate/Translate";

type CologuardQualificationIntroModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  onTakeQuestionnaire?: () => void;
};

const INFO_CARDS = [
  {
    body: "Genomic Life can help you take steps to improve or maintain your health. That includes helping you get the right cancer screening for you. Colon cancer screening is recommended for everyone 45 and older.",
    icon: QuestionCircleIcon,
    iconAlt: "Question",
    span: true,
    title: "Why you're seeing this questionnaire"
  },
  {
    body: "Colon cancer screening can detect cancer early, before symptoms appear. That's when treatment works best.",
    icon: UserLoveIcon,
    iconAlt: "Member support",
    title: "Why it's important"
  },
  {
    body: "The following questions can help determine if you're due for screening. If you are, we can help you get screened.",
    icon: PointerRightIcon,
    iconAlt: "Next steps",
    title: "What happens next"
  }
] as const;

const infoCardSx = {
  bgcolor: COLORS.WHITE,
  borderRadius: "16px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
  p: { md: 3, xs: 2 }
} as const;

const CologuardQualificationIntroModal: React.FC<
  CologuardQualificationIntroModalProps
> = ({ isOpen = false, onClose, onTakeQuestionnaire }) => {
  return (
    <ModalWrapper
      closeOnBackdropClick={true}
      isOpen={isOpen}
      onClose={onClose}
      modalContainerSx={{
        borderRadius: "24px !important",
        boxSizing: "border-box",
        flexBasis: "auto !important",
        marginBottom: { xs: "16px", sm: "32px" },
        marginLeft: { xs: "10px", sm: "30px" },
        marginRight: { xs: "10px", sm: "30px" },
        marginTop: { xs: "16px", sm: "32px" },
        maxWidth: "900px !important",
        overflow: "visible",
        padding: {
          xs: "32px 16px 24px 16px !important",
          md: "48px 40px 32px 40px !important"
        },
        outline: 0,
        width: {
          xs: "calc(100vw - 20px) !important",
          sm: "calc(100vw - 60px) !important"
        }
      }}
      sx={{
        // ModalWrapper's JSS class centers content; !important is needed to
        // win so a modal taller than a phone viewport can scroll to its top.
        alignItems: "flex-start !important",
        overflowY: "auto !important",
        "& .MuiModal-backdrop": {
          backgroundColor: `${COLORS.PRIMARY_DARKER}80`
        },
        outline: 0
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { md: 4, xs: 3 },
          width: "100%"
        }}
      >
        <Typography
          component="h2"
          variant="h5_black"
          sx={{ m: 0, textAlign: "center" }}
        >
          <Translate text="Are you up-to-date with colon cancer screening?" />
        </Typography>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              md: "minmax(0, 0.8fr) minmax(0, 1.7fr)",
              xs: "1fr"
            }
          }}
        >
          <Box
            sx={{
              bgcolor: COLORS.ACCENT3_LIGHTER,
              borderRadius: "32px 32px 8px 32px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: { md: "space-between", xs: "flex-start" },
              gap: 3,
              minHeight: { md: 360, xs: "auto" },
              p: { md: 4, xs: 3 }
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Typography
                component="h3"
                variant="body_strong_lg_black"
                sx={{ m: 0 }}
              >
                <Translate text="Did you know?" />
              </Typography>
              <Typography component="p" variant="body_lg_black" sx={{ m: 0 }}>
                <Translate text="Colon cancer is becoming more common in" />{" "}
                <Box component="em" sx={{ fontStyle: "italic" }}>
                  <Translate text="younger people" />
                </Box>
                .
              </Typography>
            </Box>
            <Typography
              component="p"
              variant="body_sm_black"
              sx={{ fontStyle: "italic", m: 0 }}
            >
              <Translate text="The good news is that screening saves lives. Plus, there are now non-invasive options." />
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: COLORS.PRIMARY_LIGHTEST,
              borderRadius: "24px",
              boxSizing: "border-box",
              display: "grid",
              gap: 2,
              gridTemplateColumns: { md: "1fr 1fr", xs: "1fr" },
              p: "32px"
            }}
          >
            {INFO_CARDS.map((card) => (
              <Box
                key={card.title}
                sx={{
                  ...infoCardSx,
                  gridColumn: {
                    md: "span" in card && card.span ? "1 / -1" : "auto",
                    xs: "1 / -1"
                  }
                }}
              >
                <Box
                  alt={card.iconAlt}
                  component="img"
                  src={card.icon}
                  sx={{ display: "block", height: 24, width: 24 }}
                />
                <Typography
                  component="h3"
                  variant="body_strong_black"
                  sx={{ m: 0 }}
                >
                  <Translate text={card.title} />
                </Typography>
                <Typography
                  component="p"
                  variant="body_sm_dark_gray"
                  sx={{ fontSize: "13px", m: 0 }}
                >
                  <Translate text={card.body} />
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: { sm: "row", xs: "column-reverse" },
            gap: 2,
            justifyContent: "flex-end"
          }}
        >
          <GLButton
            buttonType="secondary-no-border"
            onClick={onClose}
            sx={{
              typography: "body_strong_dark_gray",
              "&:hover:not(.Mui-disabled)": {
                backgroundColor: "transparent"
              }
            }}
          >
            <Typography
              component="span"
              sx={{
                color: `${COLORS.DARK_GRAY} !important`,
                typography: "body_strong_dark_gray"
              }}
            >
              <Translate text="Not right now" />
            </Typography>
          </GLButton>
          <GLButton onClick={onTakeQuestionnaire} rightIcon={<ArrowForward />}>
            <Typography variant="body_strong_white">
              <Translate text="Take questionnaire" />
            </Typography>
          </GLButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};

export default CologuardQualificationIntroModal;
